import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ActionItems } from '../components/dashboard-action-items-card/dashboard-action-items-card.component';
import { Report } from '../models/report.interface';
import { FacilityStore } from '../stores/facility.store';
import { Facility } from '../types/facility';
import { DateService } from './date.service';
import { StorageKeys, StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class FacilityService {
  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private storageService: StorageService,
    private facilityStore: FacilityStore,
  ) {
    const currentFacilityId = this.storageService.getItem(StorageKeys.CURRENT_FACILITY_ID) ?? undefined;
    if (currentFacilityId) {
      this.setCurrentById(currentFacilityId);
    }
  }

  setCurrentById(facilityId: string): void {
    this.getById(facilityId).subscribe((facility) => {
      this.facilityStore.setCurrentFacility(facility);
    });
  }

  /**
   * Facility
   */

  loadAll(enterpriseId: string): Promise<Facility[]> {
    return this.http
      .get<Facility[]>(environment.apiDomain + `/enterprises/enterprises/${enterpriseId}/facilities`)
      .toPromise();
  }

  getByEnterpriseId(enterpriseId: string): Observable<Facility[]> {
    return this.http.get<Facility[]>(environment.apiDomain + `/enterprises/enterprises/${enterpriseId}/facilities`);
  }

  getById(facilityId: string): Observable<Facility> {
    return this.http.get<Facility>(environment.apiDomain + `/enterprises/facilities/${facilityId}`);
  }

  update(facility: Facility): Observable<Facility> {
    return this.http.put<Facility>(environment.apiDomain + `/enterprises/facilities`, facility);
  }

  save(facility: Facility): Observable<Facility> {
    return this.http.post<Facility>(environment.apiDomain + `/enterprises/facilities`, facility);
  }

  delete(facilityId: string): Observable<void> {
    return this.http.delete<void>(environment.apiDomain + `/enterprises/facilities/${facilityId}`);
  }

  /**
   * Start Guide helpers
   */
  hasAssessmentsById(facilityId: string): Observable<boolean> {
    return this.http.get<boolean>(environment.apiDomain + `/ergonomics/facilities/${facilityId}/hasAssessments`);
  }

  facilityHasTeamMembers(): Observable<boolean> {
    return this.http.get<boolean>(environment.apiDomain + '/ergonomics/facilities/assessments');
  }

  getFacilityReport(facilityId: string, beginDate: Date, endDate: Date): Observable<Report[]> {
    const beginDateStr = this.dateService.dateToISOFormat(beginDate);
    const endDateStr = this.dateService.dateToISOFormat(endDate);

    return this.http.get<Report[]>(
      `${environment.apiDomain}/ergonomics/facilities/reports?facilityId=${facilityId}&dateBegin=${beginDateStr}&dateEnd=${endDateStr}`,
    );
  }

  getFacilityReportForEnterpriseLayer(enterpriseLayerId: string, beginDate: Date, endDate: Date): Observable<Report[]> {
    const beginDateStr = this.dateService.dateToISOFormat(beginDate);
    const endDateStr = this.dateService.dateToISOFormat(endDate);

    return this.http
      .get<
        Report[]
      >(`${environment.apiDomain}/ergonomics/facilities/reports?enterpriseLayerId=${enterpriseLayerId}&dateBegin=${beginDateStr}&dateEnd=${endDateStr}`)
      .pipe(map(this.groupFacilityReports));
  }

  getCurrentFacilityReport(facilityId: string): Observable<Report> {
    return this.http.get<Report>(
      `${environment.apiDomain}/ergonomics/facilities/reports/current?facilityId=${facilityId}`,
    );
  }

  getCurrentFacilityReportForEnterpriseLayer(enterpriseLayerId: string): Observable<Report> {
    return this.http.get<Report>(
      `${environment.apiDomain}/ergonomics/facilities/reports/current?enterpriseLayerId=${enterpriseLayerId}`,
    );
  }

  getAllAssignmentsByFacilityId(facilityId: string): Observable<ActionItems[]> {
    return this.http.get<ActionItems[]>(`${environment.apiDomain}/ergonomics/facilities/${facilityId}/assignments`);
  }

  private groupFacilityReports(facilityReports: Report[]) {
    const facilityReportsMap: { [key: string]: Report } = {};
    const facilitiesPerMonthMap: Map<string, number> = new Map();

    facilityReports = facilityReports || [];

    facilityReports.forEach((facilityReport) => {
      const year = new Date(facilityReport.dateCreated).getFullYear();
      const month = new Date(facilityReport.dateCreated).getMonth();
      const key = `${year}_${month}`;

      if (!facilityReportsMap[key]) {
        facilityReportsMap[key] = {
          numJobScreensCompletedThisMonth: 0,
          percentJobsAssessedForRisk: 0,
          numImprovementsScheduledThisMonth: 0,
          percentOpportunitiesWithImprovementsScheduled: 0,
          numImprovementsCompletedThisMonth: 0,
          percentImprovementsCompletedOnTime: 0,
          numOpportunitiesIdentified: 0,
          numImprovementsScheduled: 0,
          numImprovementsCompleted: 0,
          dateCreated: facilityReport.dateCreated,
        } as any;
        facilitiesPerMonthMap.set(key, 0);
      }

      facilityReportsMap[key].numJobScreensCompletedThisMonth += facilityReport.numJobScreensCompletedThisMonth;
      facilityReportsMap[key].percentJobsAssessedForRisk += facilityReport.percentJobsAssessedForRisk;
      facilityReportsMap[key].numImprovementsScheduledThisMonth += facilityReport.numImprovementsScheduledThisMonth;
      facilityReportsMap[key].percentOpportunitiesWithImprovementsScheduled +=
        facilityReport.percentOpportunitiesWithImprovementsScheduled;
      facilityReportsMap[key].numImprovementsCompletedThisMonth += facilityReport.numImprovementsCompletedThisMonth;
      facilityReportsMap[key].percentImprovementsCompletedOnTime += facilityReport.percentImprovementsCompletedOnTime;
      facilityReportsMap[key].numOpportunitiesIdentified += facilityReport.numOpportunitiesIdentified;
      facilityReportsMap[key].numImprovementsScheduled += facilityReport.numImprovementsScheduled;
      facilityReportsMap[key].numImprovementsCompleted += facilityReport.numImprovementsCompleted;

      if (facilityReport.numJobs !== undefined) {
        if (facilityReportsMap[key].numJobs === undefined) {
          facilityReportsMap[key].numJobs = 0;
        }

        facilityReportsMap[key].numJobs += facilityReport.numJobs;
      }

      if (facilityReport.numJobsAssessedForRisk !== undefined) {
        if (facilityReportsMap[key].numJobsAssessedForRisk === undefined) {
          facilityReportsMap[key].numJobsAssessedForRisk = 0;
        }

        facilityReportsMap[key].numJobsAssessedForRisk += facilityReport.numJobsAssessedForRisk;
      }

      if (facilityReport.numImprovementOpportunities !== undefined) {
        if (facilityReportsMap[key].numImprovementOpportunities === undefined) {
          facilityReportsMap[key].numImprovementOpportunities = 0;
        }

        facilityReportsMap[key].numImprovementOpportunities += facilityReport.numImprovementOpportunities;
      }

      if (facilityReport.numImprovementsCompletedOnTime !== undefined) {
        if (facilityReportsMap[key].numImprovementsCompletedOnTime === undefined) {
          facilityReportsMap[key].numImprovementsCompletedOnTime = 0;
        }

        facilityReportsMap[key].numImprovementsCompletedOnTime += facilityReport.numImprovementsCompletedOnTime;
      }

      facilitiesPerMonthMap.set(key, facilitiesPerMonthMap.get(key)! + 1);
    });

    const facilityReportsGrouped = Object.values(facilityReportsMap);

    facilityReportsGrouped.forEach((fr) => {
      const year = new Date(fr.dateCreated).getFullYear();
      const month = new Date(fr.dateCreated).getMonth();
      const key = `${year}_${month}`;
      const numFacilitiesThatMonth = facilitiesPerMonthMap.get(key)!;

      const safeDivision = (numerator: number, denominator: number) => {
        return denominator > 0 ? numerator / denominator : 0;
      };

      if (fr.numJobs !== undefined && fr.numJobsAssessedForRisk !== undefined) {
        fr.percentJobsAssessedForRisk = safeDivision(fr.numJobsAssessedForRisk, fr.numJobs);
      } else {
        fr.percentJobsAssessedForRisk = fr.percentJobsAssessedForRisk / numFacilitiesThatMonth;
      }

      if (fr.numImprovementOpportunities !== undefined && fr.numOpportunitiesIdentified !== undefined) {
        const numOpportunitiesWithScheduledProjects = fr.numImprovementOpportunities - fr.numOpportunitiesIdentified;
        fr.percentOpportunitiesWithImprovementsScheduled = safeDivision(
          numOpportunitiesWithScheduledProjects,
          fr.numOpportunitiesIdentified,
        );
      } else {
        fr.percentOpportunitiesWithImprovementsScheduled =
          fr.percentOpportunitiesWithImprovementsScheduled / numFacilitiesThatMonth;
      }

      if (fr.numImprovementsCompletedOnTime !== undefined && fr.numImprovementsCompleted !== undefined) {
        fr.percentImprovementsCompletedOnTime = safeDivision(
          fr.numImprovementsCompletedOnTime,
          fr.numImprovementsCompleted,
        );
      } else {
        fr.percentImprovementsCompletedOnTime = fr.percentImprovementsCompletedOnTime / numFacilitiesThatMonth;
      }
    });

    return facilityReportsGrouped;
  }
}
